<template>
  <div class="rentme-accept">
    <div v-if="isLoading" class="loading">
      <i class="fas fa-spinner fa-spin"></i>
      <p>Loading...</p>
    </div>

    <template v-else>
    <div v-if="!isValid" class="blank"></div>

    <div v-else-if="showDismissed" class="message-container">
      <h2>Cancelled</h2>
      <p>You chose not to accept this rental.</p>
    </div>

    <div v-else-if="showSuccess" class="message-container success">
      <i class="fas fa-check-circle icon"></i>
      <h2>Thank You!</h2>
      <p>{{ successMessage }}</p>
      <p class="contact-info">
        Contact
        <a href="mailto:communications@bigpropertymanagement.com">
          communications@bigpropertymanagement.com
        </a>
        with questions.
      </p>
    </div>

    <div v-else-if="itemData?.alreadySigned" class="message-container">
      <i class="fas fa-info-circle icon"></i>
      <h2>Already Signed</h2>
      <p>This item has already been rented by <strong>{{ itemData.signedBy }}</strong>.</p>
      <div class="item-summary">
        <p>{{ itemData.item.address }}</p>
        <p>{{ itemData.item.item_type_label }} — {{ itemData.item.description }}</p>
        <p>${{ formatMoney(itemData.item.monthly_rate) }}/mo · {{ itemData.item.min_months }} month minimum</p>
      </div>
    </div>

    <div v-else-if="itemData?.alreadyPurchased" class="message-container">
      <i class="fas fa-info-circle icon"></i>
      <h2>Already Purchased</h2>
      <p>This item has already been purchased by <strong>{{ itemData.purchasedBy }}</strong>.</p>
      <div class="item-summary">
        <p>{{ itemData.item.address }}</p>
        <p>{{ itemData.item.item_type_label }} — {{ itemData.item.description }}</p>
        <p v-if="itemData.item.purchase_amt">
          Purchase price: ${{ formatMoney(itemData.item.purchase_amt) }}
        </p>
      </div>
    </div>

    <div v-else class="form-container">
      <div class="form-header">
        <h2>Rent Me</h2>
        <p class="item-address">{{ itemData.item.address }}</p>
      </div>

      <div class="form-body">
      <div class="item-details">
        <div class="detail-row">
          <span class="label">Type:</span>
          <span>{{ itemData.item.item_type_label }}</span>
        </div>
        <div class="detail-row">
          <span class="label">Description:</span>
          <span>{{ itemData.item.description }}</span>
        </div>
        <div class="detail-row">
          <span class="label">Rate:</span>
          <span>${{ formatMoney(itemData.item.monthly_rate) }}/mo</span>
        </div>
        <div class="detail-row">
          <span class="label">Minimum:</span>
          <span>{{ itemData.item.min_months }} months</span>
        </div>
        <div class="detail-row">
          <span>
            From: {{ formatDate(itemData.item.starts_on) }} to
            {{ formatDate(itemData.item.ends_on) }}
          </span>
        </div>
        <template v-if="itemData.item.serial_no || itemData.item.model_no">
          <hr class="detail-divider" />
          <div class="detail-row detail-row-split">
            <span v-if="itemData.item.serial_no" class="detail-split-item">
              <span class="label">Ser #</span>
              <span>{{ itemData.item.serial_no }}</span>
            </span>
            <span v-if="itemData.item.model_no" class="detail-split-item">
              <span class="label">Model #</span>
              <span>{{ itemData.item.model_no }}</span>
            </span>
          </div>
        </template>
      </div>

      <p v-if="itemData.item.purchasable" class="purchase-notice">
        This unit can also be purchased for:
        <strong>${{ formatMoney(itemData.item.purchase_amt) }}</strong>.
      </p>

      <div class="terms-block">
        <div class="terms-header">
          <h3>Terms &amp; Conditions</h3>
          <button
            type="button"
            class="terms-toggle"
            :aria-expanded="termsExpanded"
            @click="toggleTerms"
          >
            {{ termsExpanded ? 'hide terms' : 'show terms' }}
            <i
              class="fas"
              :class="termsExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"
            ></i>
          </button>
        </div>
        <div v-show="termsExpanded" class="terms-expanded">
          <p class="terms-body">{{ itemData.terms }}</p>
          <div class="terms-choice-buttons">
            <button type="button" class="btn-terms-rent" @click="chooseRent">
              Rent
            </button>
            <button
              v-if="itemData.item.purchasable"
              type="button"
              class="btn-terms-buy"
              @click="chooseBuy"
            >
              Buy
            </button>
          </div>
        </div>
        <p v-if="!selectedAction" class="terms-hint terms-hint-inline">
          {{ termsAcceptHint }}
        </p>
        <p v-else-if="!termsExpanded" class="terms-choice-summary">
          You chose to <strong>{{ selectedAction }}</strong>.
          <button type="button" class="terms-change-link" @click="clearActionChoice">
            Change
          </button>
        </p>
      </div>

      <div v-if="selectedAction" class="identity-form">
        <h3>Verify Your Identity</h3>
        <div class="form-row">
          <div class="form-group">
            <input
              ref="firstNameInput"
              v-model="firstName"
              type="text"
              maxlength="100"
              placeholder="First name"
            />
          </div>
          <div class="form-group">
            <input
              v-model="lastName"
              type="text"
              maxlength="100"
              placeholder="Last name"
            />
          </div>
        </div>

        <div class="form-group">
          <label>Phone (10 digits)</label>
          <div class="phone-digits">
            <input
              v-for="i in 10"
              :key="'phone' + i"
              ref="phoneDigitInputs"
              type="tel"
              maxlength="1"
              inputmode="numeric"
              pattern="[0-9]"
              class="digit-box"
              :value="phoneDigits[i - 1]"
              @input="handlePhoneDigitInput($event, i - 1)"
              @keydown="handlePhoneDigitKeydown($event, i - 1)"
              @paste="handlePhonePaste"
            />
          </div>
        </div>

        <div v-if="lockCodeLength > 0" class="form-group">
          <label>Door Lock Code ({{ lockCodeLength }} digits)</label>
          <div class="phone-digits lock-digits">
            <input
              v-for="i in lockCodeLength"
              :key="'lock' + i"
              ref="lockDigitInputs"
              type="tel"
              maxlength="1"
              inputmode="numeric"
              pattern="[0-9]"
              class="digit-box"
              :value="lockDigits[i - 1]"
              @input="handleLockDigitInput($event, i - 1)"
              @keydown="handleLockDigitKeydown($event, i - 1)"
              @paste="handleLockPaste"
            />
          </div>
        </div>

        <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
        <div v-if="purchaseErrorMessage" class="error-message">
          {{ purchaseErrorMessage }}
        </div>
        <div v-if="termsAcceptHint" class="terms-hint">{{ termsAcceptHint }}</div>

        <div v-if="selectedAction === 'buy'" class="purchase-action-row">
          <span class="purchase-agreement-text">
            I agree to purchase this unit as is for
            <strong>${{ formatMoney(itemData.item.purchase_amt) }}</strong>.
            The amount will be added to my ledger in three installments.
          </span>
          <button
            type="button"
            class="btn-buy"
            :disabled="purchasing || !canSubmitPurchase"
            @click="handlePurchase"
          >
            {{ purchasing ? 'Processing...' : 'Buy' }}
          </button>
        </div>

        <div v-if="selectedAction === 'rent'" class="button-group">
          <button
            @click="handleAccept"
            class="btn-primary"
            :disabled="submitting || !canSubmit"
            :title="termsAcceptHint || undefined"
          >
            {{ submitting ? 'Processing...' : 'Add to my Ledger' }}
          </button>
          <button @click="handleCancel" class="btn-secondary">Cancel</button>
        </div>
        <div v-else-if="selectedAction === 'buy'" class="button-group">
          <button @click="handleCancel" class="btn-secondary btn-secondary-full">
            Cancel
          </button>
        </div>
      </div>
      </div>
    </div>

    <div class="refresh-footer">
      <button
        type="button"
        class="btn-refresh"
        :disabled="refreshing"
        @click="handleRefresh"
      >
        <i class="fas fa-sync-alt" :class="{ 'fa-spin': refreshing }"></i>
        Refresh
      </button>
    </div>
    </template>
  </div>
</template>

<script>
import axiosLib from 'axios';

// Public tenant page: avoid attaching stale JWT from localStorage (expressJwt rejects it).
const api = axiosLib.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || '',
});

export default {
  name: 'RentMeAccept',
  data() {
    return {
      isLoading: true,
      isValid: false,
      itemData: null,
      firstName: '',
      lastName: '',
      phoneDigits: Array(10).fill(''),
      lockDigits: [],
      lockCodeLength: 0,
      errorMessage: '',
      purchaseErrorMessage: '',
      submitting: false,
      purchasing: false,
      refreshing: false,
      showSuccess: false,
      showDismissed: false,
      successMessage: '',
      itemId: null,
      securityKey: null,
      termsExpanded: false,
      termsAccepted: false,
      selectedAction: null,
    };
  },
  computed: {
    phoneString() {
      return this.phoneDigits.join('');
    },
    lockCodeString() {
      return this.lockDigits.join('');
    },
    phoneComplete() {
      return this.phoneDigits.every((d) => d !== '');
    },
    lockCodeComplete() {
      if (this.lockCodeLength === 0) return true;
      return this.lockDigits.every((d) => d !== '');
    },
    canSubmit() {
      return (
        this.selectedAction === 'rent' &&
        this.termsAccepted &&
        this.firstName.trim() &&
        this.lastName.trim() &&
        this.phoneComplete &&
        this.lockCodeComplete
      );
    },
    canSubmitPurchase() {
      return (
        this.selectedAction === 'buy' &&
        this.firstName.trim() &&
        this.lastName.trim() &&
        this.phoneComplete &&
        this.lockCodeComplete
      );
    },
    termsAcceptHint() {
      if (this.selectedAction) return '';
      return 'Please open the Terms & Conditions and choose Rent or Buy.';
    },
  },
  mounted() {
    this.itemId = this.$route.query.id;
    this.securityKey = this.$route.query.key;
    this.validateItem();
  },
  methods: {
    toggleTerms() {
      this.termsExpanded = !this.termsExpanded;
    },

    chooseRent() {
      this.selectedAction = 'rent';
      this.termsAccepted = true;
      this.termsExpanded = false;
      this.errorMessage = '';
      this.purchaseErrorMessage = '';
      this.$nextTick(() => {
        this.$refs.firstNameInput?.focus();
      });
    },

    chooseBuy() {
      this.selectedAction = 'buy';
      this.termsAccepted = true;
      this.termsExpanded = false;
      this.errorMessage = '';
      this.purchaseErrorMessage = '';
      this.$nextTick(() => {
        this.$refs.firstNameInput?.focus();
      });
    },

    clearActionChoice() {
      this.selectedAction = null;
      this.termsAccepted = false;
      this.termsExpanded = true;
      this.errorMessage = '';
      this.purchaseErrorMessage = '';
    },

    async handleRefresh() {
      if (this.refreshing || this.isLoading) return;
      this.refreshing = true;
      this.showSuccess = false;
      this.showDismissed = false;
      this.errorMessage = '';
      this.purchaseErrorMessage = '';
      this.submitting = false;
      this.purchasing = false;
      this.termsExpanded = false;
      this.termsAccepted = false;
      this.selectedAction = null;
      this.isLoading = true;
      this.isValid = false;
      this.itemData = null;
      try {
        await this.validateItem();
      } finally {
        this.refreshing = false;
      }
    },

    async validateItem() {
      if (!this.itemId || !this.securityKey) {
        this.isLoading = false;
        this.isValid = false;
        return;
      }

      try {
        const response = await api.get('/api/public/rent-me/validate', {
          params: { id: this.itemId, key: this.securityKey },
        });
        const data = response.data;

        if (!data || data === '' || !data.valid) {
          this.isValid = false;
          return;
        }

        this.itemData = data;
        this.isValid = true;
        this.lockCodeLength = data.lockCodeLength || 0;
        this.lockDigits = Array(this.lockCodeLength).fill('');
      } catch (err) {
        this.isValid = false;
      } finally {
        this.isLoading = false;
      }
    },

    handlePhoneDigitInput(event, index) {
      const val = event.target.value.replace(/\D/g, '');
      if (val.length === 0) {
        this.phoneDigits[index] = '';
        return;
      }
      this.phoneDigits[index] = val.charAt(0);
      if (index < 9) {
        this.$nextTick(() => this.$refs.phoneDigitInputs[index + 1]?.focus());
      }
    },

    handlePhoneDigitKeydown(event, index) {
      if (event.key === 'Backspace') {
        if (this.phoneDigits[index] === '' && index > 0) {
          this.phoneDigits[index - 1] = '';
          this.$nextTick(() => this.$refs.phoneDigitInputs[index - 1]?.focus());
        } else {
          this.phoneDigits[index] = '';
        }
        event.preventDefault();
      }
    },

    handlePhonePaste(event) {
      const paste = (event.clipboardData || window.clipboardData)
        .getData('text')
        .replace(/\D/g, '');
      const start = paste.length === 11 && paste.startsWith('1') ? 1 : 0;
      for (let i = 0; i < 10 && start + i < paste.length; i++) {
        this.phoneDigits[i] = paste.charAt(start + i);
      }
      event.preventDefault();
      const nextEmpty = this.phoneDigits.findIndex((d) => d === '');
      if (nextEmpty >= 0) {
        this.$nextTick(() => this.$refs.phoneDigitInputs[nextEmpty]?.focus());
      }
    },

    handleLockDigitInput(event, index) {
      const val = event.target.value.replace(/\D/g, '');
      if (val.length === 0) {
        this.lockDigits[index] = '';
        return;
      }
      this.lockDigits[index] = val.charAt(0);
      if (index < this.lockCodeLength - 1) {
        this.$nextTick(() => this.$refs.lockDigitInputs[index + 1]?.focus());
      }
    },

    handleLockDigitKeydown(event, index) {
      if (event.key === 'Backspace') {
        if (this.lockDigits[index] === '' && index > 0) {
          this.lockDigits[index - 1] = '';
          this.$nextTick(() => this.$refs.lockDigitInputs[index - 1]?.focus());
        } else {
          this.lockDigits[index] = '';
        }
        event.preventDefault();
      }
    },

    handleLockPaste(event) {
      const paste = (event.clipboardData || window.clipboardData)
        .getData('text')
        .replace(/\D/g, '')
        .slice(0, this.lockCodeLength);
      for (let i = 0; i < this.lockCodeLength; i++) {
        this.lockDigits[i] = paste[i] || '';
      }
      event.preventDefault();
    },

    async handlePurchase() {
      if (!this.canSubmitPurchase || this.purchasing) return;
      this.purchaseErrorMessage = '';
      this.purchasing = true;

      try {
        const payload = {
          id: this.itemId,
          key: this.securityKey,
          firstName: this.firstName.trim(),
          lastName: this.lastName.trim(),
          phone: this.phoneString,
          lockCode: this.lockCodeLength > 0 ? this.lockCodeString : undefined,
        };

        const { data } = await api.post('/api/public/rent-me/purchase', payload);

        if (data.alreadyPurchased) {
          this.itemData.alreadyPurchased = true;
          this.itemData.purchasedBy = data.purchasedBy;
          return;
        }

        this.successMessage =
          data.message ||
          `Purchase accepted for ${data.fullName}. Three installments have been posted to your ledger.`;
        this.showSuccess = true;
      } catch (err) {
        this.purchaseErrorMessage =
          err.response?.data?.error ||
          'Unable to complete purchase. Please verify your information and try again.';
      } finally {
        this.purchasing = false;
      }
    },

    async handleAccept() {
      if (!this.canSubmit || this.submitting) return;
      this.errorMessage = '';
      this.purchaseErrorMessage = '';
      this.submitting = true;

      try {
        const payload = {
          id: this.itemId,
          key: this.securityKey,
          firstName: this.firstName.trim(),
          lastName: this.lastName.trim(),
          phone: this.phoneString,
          lockCode: this.lockCodeLength > 0 ? this.lockCodeString : undefined,
        };

        const { data } = await api.post('/api/public/rent-me/accept', payload);

        if (data.alreadySigned) {
          this.itemData.alreadySigned = true;
          this.itemData.signedBy = data.signedBy;
          return;
        }

        this.successMessage =
          data.message ||
          `Rental accepted for ${data.fullName}. The first month has been posted to your ledger.`;
        this.showSuccess = true;
      } catch (err) {
        this.errorMessage =
          err.response?.data?.error ||
          'Unable to complete rental. Please verify your information and try again.';
      } finally {
        this.submitting = false;
      }
    },

    handleCancel() {
      this.showDismissed = true;
    },

    formatMoney(val) {
      if (val == null) return '0.00';
      return parseFloat(val).toFixed(2);
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      const dateOnly = String(dateStr).match(/^(\d{4}-\d{2}-\d{2})/)?.[1];
      const d = dateOnly
        ? new Date(`${dateOnly}T12:00:00`)
        : new Date(dateStr);
      return d.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
      });
    },
  },
};
</script>

<style scoped>
.rentme-accept {
  width: 100%;
  max-width: 440px;
  margin: 0 auto;
  padding: 12px;
  box-sizing: border-box;
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu,
    Cantarell, sans-serif;
  flex: 1;
  min-height: 0;
  height: 100%;
  overflow: hidden;
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.loading,
.blank {
  text-align: center;
  padding: 60px 20px;
  color: #666;
  width: 100%;
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 80vh;
}

.blank {
  min-height: 100vh;
}

.message-container {
  text-align: center;
  padding: 40px 20px;
  width: 100%;
  max-width: 440px;
  max-height: calc(100vh - 40px);
  box-sizing: border-box;
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
}

.message-container .icon {
  font-size: 2.5em;
  color: #a080a8;
  margin-bottom: 12px;
}

.message-container.success .icon {
  color: #4a9e4a;
}

.contact-info {
  margin-top: 16px;
  font-size: 0.9em;
  color: #666;
}

.contact-info a {
  color: #7a5a82;
}

.form-container {
  width: 100%;
  max-width: 440px;
  flex: 1;
  min-height: 0;
  box-sizing: border-box;
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.form-header {
  flex-shrink: 0;
  background: #a080a8;
  color: white;
  padding: 11px 16px;
  text-align: center;
  border-radius: 16px 16px 0 0;
}

.form-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 14px;
  scrollbar-width: thin;
  scrollbar-color: #c8b0cf transparent;
}

.form-body::-webkit-scrollbar {
  width: 6px;
}

.form-body::-webkit-scrollbar-thumb {
  background: #c8b0cf;
  border-radius: 3px;
}

.form-header h2 {
  margin: 0 0 4px 0;
}

.item-address {
  margin: 0;
  opacity: 0.9;
  font-size: 0.95em;
}

.item-details {
  padding: 10px 16px;
  border-bottom: 1px solid #eee;
}

.detail-row {
  display: flex;
  gap: 8px;
  margin-bottom: 4px;
  font-size: 0.95em;
}

.detail-row .label {
  font-weight: 600;
  min-width: 90px;
  color: #555;
}

.detail-row-split {
  flex-wrap: wrap;
  gap: 12px 20px;
}

.detail-split-item {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
}

.detail-split-item .label {
  min-width: auto;
}

.detail-divider {
  border: none;
  border-top: 1px solid #ddd;
  margin: 6px 0 4px;
}

.purchase-notice {
  margin: 0 16px 10px;
  padding: 9px 12px;
  background: #f3eef8;
  border: 1px solid #d8c8e4;
  border-radius: 6px;
  color: #4a3d56;
  line-height: 1.45;
}

.purchase-action-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 10px 0;
  padding: 9px 12px;
  background: #faf7fc;
  border: 1px solid #ddd0e8;
  border-radius: 6px;
}

.purchase-agreement-text {
  flex: 1;
  color: #333;
  line-height: 1.45;
  font-size: 0.95em;
}

.btn-buy {
  flex-shrink: 0;
  background: #6e5875;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 10px 18px;
  font-weight: 600;
  cursor: pointer;
}

.btn-buy:hover:not(:disabled) {
  background: #5a4760;
}

.btn-buy:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.terms-block {
  padding: 10px 16px;
  background: #faf8fb;
  border-bottom: 1px solid #eee;
}

.terms-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.terms-block h3 {
  margin: 0;
  font-size: 0.95em;
  color: #7a5a82;
}

.terms-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 6px 10px;
  border: 1px solid #c8b0cf;
  border-radius: 6px;
  background: white;
  color: #7a5a82;
  font-size: 0.82em;
  font-weight: 600;
  cursor: pointer;
}

.terms-toggle:hover {
  background: #f3edf5;
}

.terms-expanded {
  margin-top: 8px;
}

.terms-body {
  margin: 0 0 10px;
  font-size: 0.88em;
  line-height: 1.5;
  color: #444;
  white-space: pre-wrap;
}

.terms-choice-buttons {
  display: flex;
  gap: 10px;
}

.btn-terms-rent,
.btn-terms-buy {
  flex: 1;
  padding: 10px 14px;
  border: none;
  border-radius: 6px;
  font-size: 0.95em;
  font-weight: 600;
  cursor: pointer;
}

.btn-terms-rent {
  background: #a080a8;
  color: white;
}

.btn-terms-rent:hover {
  background: #8d6f95;
}

.btn-terms-buy {
  background: #6e5875;
  color: white;
}

.btn-terms-buy:hover {
  background: #5a4760;
}

.terms-choice-summary {
  margin: 8px 0 0;
  font-size: 0.88em;
  color: #555;
}

.terms-change-link {
  margin-left: 6px;
  padding: 0;
  border: none;
  background: none;
  color: #7a5a82;
  font-size: inherit;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
}

.identity-form {
  padding: 10px 16px 6px;
}

.identity-form h3 {
  margin: 0 0 8px 0;
  font-size: 0.95em;
  color: #7a5a82;
}

.form-row {
  display: flex;
  gap: 10px;
  margin-bottom: 8px;
}

.form-group {
  flex: 1;
  margin-bottom: 8px;
}

.form-group label {
  display: block;
  font-weight: 600;
  font-size: 0.85em;
  margin-bottom: 4px;
  color: #555;
}

.form-group input[type='text'] {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
  font-size: 16px;
}

.phone-digits {
  display: flex;
  gap: 3px;
  flex-wrap: nowrap;
  max-width: 100%;
}

.digit-box {
  box-sizing: border-box;
  flex: 0 0 28px;
  width: 28px;
  height: 40px;
  text-align: center;
  font-size: 1.05em;
  border: 2px solid #bbb;
  border-radius: 4px;
  padding: 0;
}

.digit-box:focus {
  border-color: #a080a8;
  outline: none;
}

.error-message {
  color: #c00;
  font-size: 0.9em;
  margin-bottom: 8px;
}

.terms-hint {
  color: #7a5a82;
  font-size: 0.88em;
  margin-bottom: 8px;
  line-height: 1.4;
}

.terms-hint-inline {
  margin: 8px 0 0;
}

.button-group {
  display: flex;
  gap: 10px;
}

.btn-primary {
  flex: 1;
  padding: 10px;
  background: #a080a8;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 1em;
  cursor: pointer;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  padding: 10px 16px;
  background: #ddd;
  color: #333;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.btn-secondary-full {
  width: 100%;
}

.item-summary {
  margin-top: 16px;
  text-align: left;
  background: #f5f5f5;
  padding: 12px;
  border-radius: 6px;
  font-size: 0.9em;
}

.refresh-footer {
  width: 100%;
  max-width: 440px;
  flex-shrink: 0;
  margin-top: 8px;
  padding-bottom: 4px;
  display: flex;
  justify-content: center;
}

.btn-refresh {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border: 1px solid #bbb;
  border-radius: 6px;
  background: white;
  color: #555;
  font-size: 0.95em;
  cursor: pointer;
}

.btn-refresh:hover:not(:disabled) {
  border-color: #a080a8;
  color: #7a5a82;
}

.btn-refresh:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 480px) {
  .rentme-accept {
    padding: 10px;
  }

  .form-container,
  .message-container {
    border-radius: 12px;
  }

  .form-header {
    border-radius: 12px 12px 0 0;
    padding: 10px 14px;
  }

  .form-body {
    padding-bottom: 18px;
  }

  .item-details,
  .terms-block {
    padding: 9px 14px;
  }

  .identity-form {
    padding: 9px 14px 5px;
  }

  .purchase-notice {
    margin: 0 14px 8px;
  }

  .phone-digits {
    gap: 2px;
  }

  .digit-box {
    flex: 0 0 26px;
    width: 26px;
    height: 38px;
    font-size: 16px;
  }

  .purchase-action-row {
    flex-direction: column;
    align-items: stretch;
  }

  .btn-buy {
    width: 100%;
  }
}
</style>